/**
 * scoringOverview — pure aggregation layer behind the Scoring Engine view
 * (Tools ▸ Scoring Engine).
 *
 * The CRM already ships two deterministic, on-demand scorers:
 *   - `crm/score.ts`   — lead heat (prospects + customers, 0–100, cold/warm/hot)
 *   - `crm/health.ts`  — live-customer health (0–100, at-risk/watch/healthy)
 *
 * This module rolls those per-record scores up into the config + analytics
 * the Scoring Engine surface renders: band thresholds (the engine's actual
 * cut-offs, mirrored here as displayable config), distribution counts,
 * averages, ranked lists, and an aggregated factor table showing WHICH
 * signals are driving scores up or down across the whole book.
 *
 * Everything is computed on demand from the live records — nothing is
 * stored, so the view can never drift from the scorers' real output.
 * Pure + DOM-free so it unit-tests under the default node environment.
 */

import type { Person, ProjectTask } from '../db/types';
import { scoreLead, type LeadBand, type LeadScore, type ScoreFactor } from './score';
import {
  rankCustomersByRisk,
  type CustomerHealth,
  type HealthBand,
  type HealthFactor,
} from './health';

// ---------------------------------------------------------------------------
// Config surface — the engines' band cut-offs, as displayable rules.
// These mirror `bandFor` in score.ts / health.ts (the single sources of
// truth for the numbers); the tests pin them against the scorers' real
// behaviour so they can't silently drift.
// ---------------------------------------------------------------------------

export interface BandRule {
  band: string;
  /** Inclusive lower bound of the band. */
  min: number;
  /** Inclusive upper bound of the band. */
  max: number;
  description: string;
}

/** Lead-score bands (crm/score.ts) — hot ≥70, warm ≥40, else cold. */
export const LEAD_BAND_RULES: readonly BandRule[] = [
  { band: 'hot', min: 70, max: 100, description: 'Late-stage, high-value, recently touched — work these first.' },
  { band: 'warm', min: 40, max: 69, description: 'Real signal but incomplete — qualify or re-touch.' },
  { band: 'cold', min: 0, max: 39, description: 'Weak or dead-end signal; closed-lost / churned pin here.' },
] as const;

/** Customer-health bands (crm/health.ts) — healthy ≥70, watch ≥40, else at-risk. */
export const HEALTH_BAND_RULES: readonly BandRule[] = [
  { band: 'healthy', min: 70, max: 100, description: 'No live risk signals of note — keep the cadence.' },
  { band: 'watch', min: 40, max: 69, description: 'Slipping — quiet account, near renewal, or overdue work.' },
  { band: 'at-risk', min: 0, max: 39, description: 'Multiple live debits — intervene now.' },
] as const;

/** AQI bands (crm/aqi.ts `aqiBand`) — strong ≥80, steady ≥55, else slipping. */
export const AQI_BAND_RULES: readonly BandRule[] = [
  { band: 'strong', min: 80, max: 100, description: 'The operation is compounding — protect the cadence.' },
  { band: 'steady', min: 55, max: 79, description: 'Working, with visible drag — fix the weakest component.' },
  { band: 'slipping', min: 0, max: 54, description: 'Neglect is winning — overdue work and stale pipeline dominate.' },
] as const;

// ---------------------------------------------------------------------------
// Lead-score roll-up
// ---------------------------------------------------------------------------

export interface ScoredLead {
  person: Person;
  score: LeadScore;
}

export interface LeadScoreSummary {
  /** Number of scorable records (prospects + customers). */
  total: number;
  /** Mean score across scorable records, rounded; 0 when empty. */
  average: number;
  /** Distribution across the three bands. */
  bands: Record<LeadBand, number>;
  /** Every scorable record with its live score, highest first. */
  ranked: ScoredLead[];
}

/**
 * Score every scorable person (prospects and customers — the categories
 * `scoreLead` actually grades) and roll the results up. Sorted hottest
 * first so the view's "top leads" list is just a slice.
 */
export function summarizeLeadScores(people: Person[]): LeadScoreSummary {
  const scorable = people.filter(
    (p) => p.category === 'prospect' || p.category === 'customer',
  );
  const ranked: ScoredLead[] = scorable
    .map((person) => ({ person, score: scoreLead(person) }))
    .sort((a, b) => b.score.score - a.score.score);

  const bands: Record<LeadBand, number> = { cold: 0, warm: 0, hot: 0 };
  let sum = 0;
  for (const r of ranked) {
    bands[r.score.band]++;
    sum += r.score.score;
  }

  return {
    total: ranked.length,
    average: ranked.length ? Math.round(sum / ranked.length) : 0,
    bands,
    ranked,
  };
}

// ---------------------------------------------------------------------------
// Customer-health roll-up
// ---------------------------------------------------------------------------

export interface ScoredCustomer {
  person: Person;
  health: CustomerHealth;
}

export interface HealthSummary {
  /** Number of live (non-churned) customers scored. */
  total: number;
  /** Mean health across live customers, rounded; 0 when empty. */
  average: number;
  bands: Record<HealthBand, number>;
  /** Live customers with their health, riskiest first. */
  ranked: ScoredCustomer[];
}

/**
 * Health roll-up over the live customer book. Delegates the actual
 * scoring + riskiest-first ranking to `rankCustomersByRisk` (churned
 * accounts excluded — they are no longer live) so the numbers here are
 * exactly what the Customer Success cockpit shows.
 */
export function summarizeCustomerHealth(
  people: Person[],
  tasks: ProjectTask[] = [],
): HealthSummary {
  const ranked = rankCustomersByRisk(people, { tasks });

  const bands: Record<HealthBand, number> = { 'at-risk': 0, watch: 0, healthy: 0 };
  let sum = 0;
  for (const r of ranked) {
    bands[r.health.band]++;
    sum += r.health.score;
  }

  return {
    total: ranked.length,
    average: ranked.length ? Math.round(sum / ranked.length) : 0,
    bands,
    ranked,
  };
}

// ---------------------------------------------------------------------------
// Factor analytics — which signals are moving scores, book-wide.
// ---------------------------------------------------------------------------

export interface FactorStat {
  label: string;
  /** How many records this factor fired on. */
  count: number;
  /** Signed sum of the factor's contributions across those records. */
  totalPoints: number;
}

export interface FactorBreakdown {
  /** Factors adding points, biggest total contribution first. */
  positive: FactorStat[];
  /** Factors subtracting points, biggest total drag first. */
  negative: FactorStat[];
}

/**
 * Aggregate per-record factor lists (from either scorer — the shapes are
 * identical) into a book-wide table: per label, how often it fired and
 * its total signed contribution. Zero-point factors are informational
 * (e.g. "Touched this week" on health) and are dropped.
 */
export function aggregateScoreFactors(
  factorLists: readonly (readonly (ScoreFactor | HealthFactor)[])[],
): FactorBreakdown {
  const byLabel = new Map<string, FactorStat>();
  for (const factors of factorLists) {
    for (const f of factors) {
      if (f.points === 0) continue;
      const cur = byLabel.get(f.label);
      if (cur) {
        cur.count += 1;
        cur.totalPoints += f.points;
      } else {
        byLabel.set(f.label, { label: f.label, count: 1, totalPoints: f.points });
      }
    }
  }
  const all = [...byLabel.values()];
  return {
    positive: all
      .filter((f) => f.totalPoints > 0)
      .sort((a, b) => b.totalPoints - a.totalPoints),
    negative: all
      .filter((f) => f.totalPoints < 0)
      .sort((a, b) => a.totalPoints - b.totalPoints),
  };
}
